import { Box, Button, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import FirstLocationRedirectHook from "@/components/FirstLocationRedirectHook.tsx";
import Loading from "@/components/Loading/Index";
import { AppContext } from "@/models/AppStateProvider.tsx";
import { forceReload } from "@/models/BootstrapUtils.ts";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider.tsx";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";

export default function FirstLocationRedirect() {
  const { flags } = React.useContext(AppContext);
  const { currentWorkspaceId, myCurrentWorkspaceMembership } = React.useContext(WorkspaceContext);
  const { myAccount } = React.useContext(MyAccountContext);
  const [clearingDb, setClearingDb] = useState(false);
  const [initialTimeout, setInitialTimeout] = useState(false);
  const navigate = useNavigate();
  const { redirectUrl, isChannelListLoading, isChannelListEmpty } = FirstLocationRedirectHook();
  const urlToRedirectTo = redirectUrl(flags?.newDesign ? "drivers" : null);

  const listReadyPrereqs = useMemo(
    () => Boolean(currentWorkspaceId && myCurrentWorkspaceMembership?.id && myAccount?.id),
    [currentWorkspaceId, myCurrentWorkspaceMembership?.id, myAccount?.id],
  );

  useEffect(() => {
    if (!listReadyPrereqs) {
      setInitialTimeout(false);
      return;
    }
    const t = window.setTimeout(() => setInitialTimeout(true), 3000);
    return () => window.clearTimeout(t);
  }, [listReadyPrereqs]);

  useEffect(() => {
    if (urlToRedirectTo) {
      navigate(urlToRedirectTo);
    }
  }, [urlToRedirectTo, navigate]);

  const onClearDatabase = () => {
    setClearingDb(true);
    void forceReload();
  };

  /** While [] may be transient (bootstrap still filling the local DB), keep the loading UI until this elapses. */
  const showLoading = !listReadyPrereqs || isChannelListLoading || (isChannelListEmpty && !initialTimeout);

  const showDbEmptyError = listReadyPrereqs && initialTimeout && isChannelListEmpty;

  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 10,
        display: "flex",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box>
        {showLoading && (
          <Box sx={{ display: "flex" }}>
            <Box sx={{ mr: 2 }}>Loading </Box>
            <Loading variant="white" size="large" baseSize={6} />
          </Box>
        )}

        {showDbEmptyError && (
          <Box sx={{ maxWidth: 420, textAlign: "center" }}>
            <Typography sx={{ mb: 2 }}>
              Local data could not load a channel list. Your local database may be out of sync. Click below to clear the
              local data, then the app will reload.
            </Typography>
            <Button disabled={clearingDb} variant="contained" color="primary" onClick={onClearDatabase}>
              {clearingDb ? "Clearing…" : "Clear local database and reload"}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
